import { useEffect, useMemo, useRef } from 'react'
import { AdvancedMarker } from '@vis.gl/react-google-maps'
import type { MiningRightMapItemResponse } from '../../../api/models/response/miningRight'
import { KMap, FitBounds, MarkerTooltip, useMap } from '../../../lib/googleMaps'
import { ComponentPin } from '../../../lib/mapMarkers'

interface Props {
  items: MiningRightMapItemResponse[]
  onSelect: (id: string) => void
}

const POLYGON_COLOR = '#b4532a'

type LL = google.maps.LatLngLiteral

interface ParsedItem {
  dm: MiningRightMapItemResponse
  positions: LL[]
}

function parsePositions(geoJson: string | null): LL[] {
  if (!geoJson) return []
  try {
    const g = JSON.parse(geoJson)
    return g.coordinates[0].map((c: number[]) => ({ lat: c[1], lng: c[0] }) as LL)
  } catch {
    return []
  }
}

/**
 * Polígono imperativo sobre `google.maps.Polygon` (la base de googleMaps no
 * expone Polygon, solo Polyline). Mismo patrón de ciclo de vida que
 * `lib/googleMaps/Polyline.tsx`: crea el objeto y lo asocia al mapa en un
 * efecto, actualiza opciones en otro y limpia con `setMap(null)`.
 */
function Polygon({
  paths,
  color,
  fillOpacity,
  weight,
  onClick,
}: {
  paths: LL[]
  color: string
  fillOpacity: number
  weight: number
  onClick?: () => void
}) {
  const map = useMap()
  const polyRef = useRef<google.maps.Polygon | null>(null)

  useEffect(() => {
    if (!map) return
    const poly = new google.maps.Polygon()
    poly.setMap(map)
    polyRef.current = poly
    return () => {
      poly.setMap(null)
      polyRef.current = null
    }
  }, [map])

  useEffect(() => {
    polyRef.current?.setOptions({
      paths,
      strokeColor: color,
      strokeWeight: weight,
      fillColor: color,
      fillOpacity,
      clickable: !!onClick,
    })
  }, [paths, color, fillOpacity, weight, onClick])

  useEffect(() => {
    const poly = polyRef.current
    if (!poly || !onClick) return
    const listener = poly.addListener('click', onClick)
    return () => listener.remove()
  }, [onClick])

  return null
}

export function ComponentesMapView({ items, onSelect }: Props) {
  const parsedItems = useMemo<ParsedItem[]>(
    () => items.map((dm) => ({ dm, positions: parsePositions(dm.boundaryGeoJson) })).filter((p) => p.positions.length > 0),
    [items],
  )

  const allPositions = useMemo(
    () => parsedItems.flatMap((p) => p.positions),
    [parsedItems],
  )

  return (
    <KMap defaultCenter={{ lat: -9.19, lng: -75.0152 }} defaultZoom={6} mapTypeId="hybrid">
      <FitBounds points={allPositions} padding={50} maxZoom={15} />

      {parsedItems.map(({ dm, positions }) => (
        <Polygon
          key={dm.id}
          paths={positions}
          color={POLYGON_COLOR}
          weight={2}
          fillOpacity={0.15}
          onClick={() => onSelect(dm.id)}
        />
      ))}

      {parsedItems.flatMap(({ dm }) =>
        dm.components
          .filter((c) => c.lat !== 0 || c.lng !== 0)
          .map((c) => (
            <AdvancedMarker
              key={c.id}
              position={{ lat: c.lat, lng: c.lng }}
              onClick={() => onSelect(dm.id)}
            >
              <MarkerTooltip title={c.componentTypeName} subtitle={c.name}>
                <ComponentPin code={c.componentTypeCode} />
              </MarkerTooltip>
            </AdvancedMarker>
          )),
      )}
    </KMap>
  )
}
